import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useTranslation } from '@parentpath/i18n';
import type { NameFilters as NameFiltersType } from '../types';

interface NameFiltersProps {
  filters: NameFiltersType;
  onUpdate: (filters: Partial<NameFiltersType>) => void;
  onReset: () => void;
  onClose: () => void;
}

const ORIGIN_KEYS = [
  'german', 'english', 'french', 'italian', 'spanish',
  'turkish', 'arabic', 'hebrew', 'greek', 'nordic', 'slavic',
] as const;

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export function NameFilters({
  filters,
  onUpdate,
  onReset,
  onClose,
}: NameFiltersProps) {
  const { t } = useTranslation('names');
  const toggleOrigin = (origin: string) => {
    const current = filters.origins;
    if (current.includes(origin)) {
      onUpdate({ origins: current.filter((o) => o !== origin) });
    } else {
      onUpdate({ origins: [...current, origin] });
    }
  };

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <h2>{t('filters.title')}</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content space-y-4">
          {/* Gender */}
          <div>
            <h3 className="text-xs font-medium text-white/80 mb-2">{t('filters.gender')}</h3>
            <div className="flex gap-1.5">
              {[
                { value: 'all', label: t('filters.genderAll') },
                { value: 'male', label: t('filters.genderBoy') },
                { value: 'female', label: t('filters.genderGirl') },
                { value: 'neutral', label: t('filters.genderUnisex') },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => onUpdate({ gender: option.value as NameFiltersType['gender'] })}
                  className={cn(
                    'px-3 py-1.5 rounded-[15px] text-xs font-medium transition-colors',
                    filters.gender === option.value
                      ? 'bg-pp-orange text-white'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Starting Letter */}
          <div>
            <h3 className="text-xs font-medium text-white/80 mb-2">{t('filters.firstLetter')}</h3>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => onUpdate({ startingLetter: null })}
                className={cn(
                  'w-8 h-8 rounded-[5px] text-xs font-medium transition-colors',
                  filters.startingLetter === null
                    ? 'bg-pp-orange text-white'
                    : 'bg-white/20 text-white hover:bg-white/30'
                )}
              >
                {t('filters.firstLetterAll')}
              </button>
              {ALPHABET.map((letter) => (
                <button
                  key={letter}
                  onClick={() => onUpdate({ startingLetter: letter })}
                  className={cn(
                    'w-8 h-8 rounded-[5px] text-xs font-medium transition-colors',
                    filters.startingLetter === letter
                      ? 'bg-pp-orange text-white'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  )}
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>

          {/* Origins */}
          <div>
            <h3 className="text-xs font-medium text-white/80 mb-2">{t('filters.origin')}</h3>
            <div className="flex flex-wrap gap-1.5">
              {ORIGIN_KEYS.map((originKey) => (
                <button
                  key={originKey}
                  onClick={() => toggleOrigin(originKey)}
                  className={cn(
                    'px-2.5 py-1 rounded-[15px] text-xs font-medium transition-colors',
                    filters.origins.includes(originKey)
                      ? 'bg-pp-orange text-white'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  )}
                >
                  {t(`origins.${originKey}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Popularity */}
          <div>
            <h3 className="text-xs font-medium text-white/80 mb-2">{t('filters.popularity')}</h3>
            <div className="flex gap-1.5">
              {[
                { value: null, label: t('filters.popularityAll') },
                { value: 10, label: t('filters.popularityTop10') },
                { value: 50, label: t('filters.popularityTop50') },
                { value: 100, label: t('filters.popularityTop100') },
              ].map((option) => (
                <button
                  key={option.value ?? 'all'}
                  onClick={() => onUpdate({ popularityMax: option.value })}
                  className={cn(
                    'px-3 py-1.5 rounded-[15px] text-xs font-medium transition-colors',
                    filters.popularityMax === option.value
                      ? 'bg-pp-orange text-white'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            <Button
              variant="secondary"
              onClick={onReset}
              className="flex-1 bg-white/20 text-white border-white/25 hover:bg-white/30"
            >
              {t('filters.reset')}
            </Button>
            <Button onClick={onClose} className="flex-1 bg-pp-orange hover:bg-pp-orange/90">
              {t('filters.apply')}
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
